if (!customElements.get('recipient-form')) {
  customElements.define(
    'recipient-form',
    class RecipientForm extends HTMLElement {
      constructor() {
        super();

        this.recipientFieldsLiveRegion = this.querySelector(`#Recipient-fields-live-region-${this.dataset.sectionId}`);
        this.checkboxInput = this.querySelector(`#Recipient-checkbox-${this.dataset.sectionId}`);

        if (this.checkboxInput) this.checkboxInput.disabled = false;

        this.hiddenControlField = this.querySelector(`#Recipient-control-${this.dataset.sectionId}`);
        if (this.hiddenControlField) this.hiddenControlField.disabled = true;

        this.emailInput = this.querySelector(`#Recipient-email-${this.dataset.sectionId}`);
        this.nameInput = this.querySelector(`#Recipient-name-${this.dataset.sectionId}`);
        this.messageInput = this.querySelector(`#Recipient-message-${this.dataset.sectionId}`);
        this.sendonInput = this.querySelector(`#Recipient-send-on-${this.dataset.sectionId}`);
        this.offsetProperty = this.querySelector(`#Recipient-timezone-offset-${this.dataset.sectionId}`);

        if (this.offsetProperty) {
          this.offsetProperty.value = new Date().getTimezoneOffset().toString();
        }

        this.errorMessageWrapper = this.querySelector('.product-form__recipient-error-message-wrapper');
        this.errorMessageList = this.errorMessageWrapper?.querySelector('ul');
        this.errorMessage = this.errorMessageWrapper?.querySelector('.error-message');

        this.currentProductVariantId = this.dataset.productVariantId;

        this.addEventListener('change', this.onChange.bind(this));
        this.onChange();
      }

      connectedCallback() {}
      disconnectedCallback() {}

      onChange() {
        if (!this.checkboxInput) return;

        if (this.checkboxInput.checked) {
          this.enableInputFields();
          if (this.recipientFieldsLiveRegion && window.accessibilityStrings) {
            this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormExpanded;
          }
        } else {
          this.clearInputFields();
          this.disableInputFields();
          this.clearErrorMessage();
          if (this.recipientFieldsLiveRegion && window.accessibilityStrings) {
            this.recipientFieldsLiveRegion.innerText = window.accessibilityStrings.recipientFormCollapsed;
          }
        }
      }

      inputFields() {
        return [this.emailInput, this.nameInput, this.messageInput, this.sendonInput].filter(Boolean);
      }

      disableableFields() {
        return [...this.inputFields(), this.offsetProperty].filter(Boolean);
      }

      clearInputFields() {
        this.inputFields().forEach((field) => (field.value = ''));
      }

      enableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = false));
      }

      disableInputFields() {
        this.disableableFields().forEach((field) => (field.disabled = true));
      }

      validateFields() {
        this.clearErrorMessage();

        let errors = [];

        if (!this.nameInput?.value.trim()) {
          errors.push({ field: this.nameInput, message: "This field is required." });
        }

        if (!this.emailInput?.value.trim()) {
          errors.push({ field: this.emailInput, message: "This field is required." });
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(this.emailInput.value)) {
          errors.push({ field: this.emailInput, message: "Please enter a valid email address." });
        }

        if (errors.length) {
          errors.forEach((err) => {
            err.field.setAttribute("aria-invalid", true);
            err.field.classList.add('field--error');

            // Use the existing Shopify form__message div
            const fieldWrapper = err.field.closest('.recipient-fields__field');
            const formMessage = fieldWrapper?.querySelector('.form__message');
            const errorSpan = formMessage?.querySelector('.error-message');

            if (formMessage && errorSpan) {
              errorSpan.textContent = err.message;
              formMessage.classList.remove('hidden');
            }
          });

          return false;
        }

        return true;
      }

      // Show server-side errors returned from Shopify after cart add
      displayServerErrors(description) {
        this.clearErrorMessage();

        if (!this.errorMessageWrapper) return;

        // Friendly label map to fix "email Email can't be blank" style messages
        const fieldLabelMap = {
          email: "Recipient email",
          name: "Recipient name",
        };

        const fieldInputMap = {
          email: this.emailInput,
          name: this.nameInput,
        };

        let errors = [];

        if (typeof description === 'object' && description !== null) {
          Object.entries(description).forEach(([key, msg]) => {
            const label = fieldLabelMap[key] || key;
            errors.push({
              field: fieldInputMap[key] || null,
              message: `${label} ${msg}`
            });
          });
        } else if (typeof description === 'string') {
          errors.push({ field: null, message: description });
        }

        if (!errors.length) return;

        this.errorMessageWrapper.hidden = false;

        if (this.errorMessage) {
          this.errorMessage.innerText = "The following fields are required:";
        }

        errors.forEach((err) => {
          if (this.errorMessageList) {
            this.errorMessageList.appendChild(
              this.createErrorListItem(err.field ? `#${err.field.id}` : '#', err.message)
            );
          }

          if (err.field) {
            err.field.setAttribute("aria-invalid", true);
            err.field.classList.add('field--error');
          }
        });
      }

      createErrorListItem(target, message) {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.setAttribute('href', target);
        a.innerText = message;
        li.appendChild(a);
        li.className = 'error-message';
        return li;
      }

      clearErrorMessage() {
        // Clear the summary box if it exists
        if (this.errorMessageWrapper) {
          this.errorMessageWrapper.hidden = true;
        }

        if (this.errorMessageList) {
          this.errorMessageList.innerHTML = '';
        }

        // Clear all inline field errors
        this.querySelectorAll('.recipient-inline-error').forEach(el => el.remove());

        // ✅ Add these two lines — hide all form__message elements and clear their text
        this.querySelectorAll('.form__message').forEach(el => {
          el.classList.add('hidden');
          const errorSpan = el.querySelector('.error-message');
          if (errorSpan) errorSpan.textContent = '';
        });

        // Reset field states
        [this.emailInput, this.nameInput, this.messageInput, this.sendonInput].forEach((input) => {
          if (!input) return;
          input.setAttribute('aria-invalid', false);
          input.removeAttribute('aria-describedby');
          input.classList.remove('field--error');
        });
      }

      resetRecipientForm() {
        if (this.checkboxInput?.checked) {
          this.checkboxInput.checked = false;
          this.clearInputFields();
          this.clearErrorMessage();
        }
      }
    }
  );
}

document.addEventListener("DOMContentLoaded", function () {
  const form = document.querySelector('form[action="/cart/add"]');
  if (!form) return;

  form.addEventListener("submit", function(e) {
    const recipientForm = document.querySelector('recipient-form');
    const giftCheckbox = document.querySelector('[id^="Recipient-checkbox"]');

    if (!giftCheckbox || !giftCheckbox.checked) return;

    const isValid = recipientForm.validateFields();

    if (!isValid) {
      e.preventDefault();
      return false;
    }
  });
});